import { useEffect, useState } from 'react';
import { Storage } from '../lib/storage';
import { APP_VERSION } from '../data/changelog';
import {
  isNativeShell,
  onNativeMessage,
  postToNative,
  type NativeToWeb,
  type PushPermission,
} from '../lib/nativeBridge';

// ─────────────────────────────────────────────────────────────────────
// usePushRegistration — push notifications for the member app when it runs
// inside the native shell (apps/mobile).
//
// The shell owns the OS permission + the Expo push token; this hook asks for
// them over the bridge and stores the token per member (Storage-first) under
// `mobile_push_tokens_v1`, so a later sender job can look up who to notify.
// Outside the shell the status is 'unsupported' and nothing is sent.
// ─────────────────────────────────────────────────────────────────────

export const PUSH_TOKENS_KEY = 'mobile_push_tokens_v1';

export type PushStatus = PushPermission | 'unsupported' | 'unknown';

export interface PushTokenRecord {
  token: string;
  platform: 'ios' | 'android';
  updatedAt: string;
}

export type PushTokenMap = Record<string, PushTokenRecord>;

/** Pure: merge one member's token into the stored map (other members untouched). */
export function withPushToken(
  map: PushTokenMap | null | undefined,
  memberId: string | number,
  rec: PushTokenRecord,
): PushTokenMap {
  return { ...(map || {}), [String(memberId)]: rec };
}

async function readTokens(): Promise<PushTokenMap> {
  try {
    const res = await Storage.get(PUSH_TOKENS_KEY);
    const parsed = res?.value ? JSON.parse(res.value) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Storage-first: persist the member's token. Exported for tests. */
export async function savePushToken(
  memberId: string | number,
  rec: PushTokenRecord,
): Promise<PushTokenMap> {
  const next = withPushToken(await readTokens(), memberId, rec);
  await Storage.set(PUSH_TOKENS_KEY, JSON.stringify(next));
  return next;
}

export function usePushRegistration(memberId: string | number | null | undefined) {
  const supported = isNativeShell();
  const [status, setStatus] = useState<PushStatus>(supported ? 'unknown' : 'unsupported');
  const [error, setError] = useState<string | null>(null);

  // Tell the shell we are up (it replies with `native`), ask for the current
  // permission, and keep listening for the answers. Re-subscribes when the
  // member changes so a token is always stored against the right player.
  useEffect(() => {
    if (!supported) return undefined;
    const off = onNativeMessage((msg: NativeToWeb) => {
      if (msg.type !== 'pushStatus') return;
      (async () => {
        if (msg.token && memberId != null) {
          try {
            await savePushToken(memberId, {
              token: msg.token,
              platform: msg.platform,
              updatedAt: new Date().toISOString(),
            });
          } catch {
            /* keep the status even if the store write failed */
          }
        }
        setError(msg.error || null);
        setStatus(msg.status);
      })();
    });
    postToNative({ type: 'ready', version: APP_VERSION });
    postToNative({ type: 'getPushStatus' });
    return off;
  }, [supported, memberId]);

  const enable = () => {
    if (!supported) return;
    postToNative({ type: 'requestPushPermission' });
  };

  return { supported, status, error, enable };
}
